import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { API, axios } from "@/api";
import { ChevronRight, Sparkles, MapPin } from "lucide-react";

const CATEGORY_EMOJIS = {
  "Cannabis and Dispensaries": "\u{1F33F}", "Nightlife": "\u{1F319}",
  "Firearms and Ranges": "\u{1F3AF}", "Adult Entertainment": "\u{1F51E}",
  "Wellness and Spa": "\u{2728}", "Experiences and Travel": "\u{2708}\u{FE0F}",
};

export default function AgeUnlockScreen() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/user/age-unlock`).then(res => {
      if (res.data?.has_unlock) setData(res.data);
      else navigate("/", { replace: true });
    }).catch(() => navigate("/", { replace: true }))
    .finally(() => setLoading(false));
  }, [navigate]);

  const handleSeeAll = () => {
    const filter = data?.transition_type === "becomes_21" ? "21_plus" : "18_plus";
    navigate(`/?age_unlock=${filter}`, { replace: true });
  };

  const handleSkip = () => navigate("/", { replace: true });

  if (loading || !data) return null;

  const is21 = data.transition_type === "becomes_21";

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-[300] bg-slate-950 flex flex-col" data-testid="age-unlock-screen">
      {/* Particle animation area */}
      <div className="relative h-32 overflow-hidden flex items-center justify-center">
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div key={i}
            className="absolute w-1.5 h-1.5 rounded-full bg-amber-400"
            initial={{ x: Math.random() * 300 - 150, y: -20, opacity: 0 }}
            animate={{ y: [0, 120], opacity: [0, 1, 0], x: Math.random() * 300 - 150 }}
            transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
          />
        ))}
      </div>

      {/* Big number */}
      <div className="text-center -mt-8">
        <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", delay: 0.3, duration: 0.8 }}
          className="text-8xl font-black text-amber-400" data-testid="unlock-age-number">
          {is21 ? "21" : "18"}
        </motion.div>
      </div>

      {/* Message */}
      <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.6 }}
        className="text-center px-8 mt-4">
        <p className="text-white text-lg font-semibold" data-testid="unlock-message">
          {is21 ? "You are 21." : "You are 18."} A whole new world of birthday deals just unlocked for you.
        </p>
      </motion.div>

      {/* Newly unlocked categories */}
      {data.newly_unlocked_categories?.length > 0 && (
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.9 }}
          className="mt-6 px-4">
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2" data-testid="unlocked-categories">
            {data.newly_unlocked_categories.map(cat => (
              <div key={cat} className="flex-shrink-0 bg-slate-800/80 rounded-2xl p-4 w-40 border border-amber-500/20">
                <div className="text-2xl mb-2">{CATEGORY_EMOJIS[cat] || "\u{1F389}"}</div>
                <div className="text-sm font-semibold text-white mb-2">{cat}</div>
                <button onClick={() => navigate(`/?category=${encodeURIComponent(cat)}`)}
                  className="text-xs text-amber-400 font-medium flex items-center gap-1" data-testid={`see-deals-${cat}`}>
                  See Deals <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Sample businesses */}
      {data.sample_businesses?.length > 0 && (
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 1.2 }}
          className="mt-6 px-4 flex-1 overflow-hidden">
          <p className="text-xs text-slate-400 font-medium mb-2">Birthday deals near you that just unlocked</p>
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2" data-testid="sample-businesses">
            {data.sample_businesses.map((biz, i) => (
              <div key={i} className="flex-shrink-0 bg-slate-800/60 rounded-xl p-3 w-48 border border-slate-700">
                <div className="text-sm font-semibold text-white truncate">{biz.name}</div>
                <div className="inline-block px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] font-medium mt-1">{biz.category}</div>
                {biz.birthday_offer_title && <div className="text-xs text-slate-300 mt-1.5 truncate">{biz.birthday_offer_title}</div>}
                {biz.distance && <div className="flex items-center gap-1 text-[10px] text-slate-400 mt-1"><MapPin className="w-3 h-3" />{biz.distance}</div>}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Actions */}
      <div className="px-6 pb-8 mt-auto space-y-3">
        <button onClick={handleSeeAll} className="w-full py-3.5 rounded-2xl bg-amber-500 text-white text-sm font-bold shadow-lg active:scale-[0.98] transition-transform flex items-center justify-center gap-2" data-testid="see-all-new-deals">
          <Sparkles className="w-4 h-4" /> See All New Deals
        </button>
        <button onClick={handleSkip} className="w-full py-2 text-slate-500 text-xs font-medium" data-testid="unlock-skip">Skip for now</button>
      </div>
    </motion.div>
  );
}

// Banner for becomes_18 notification tap
export function Age18UnlockBanner({ onDismiss }) {
  return (
    <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }}
      className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center justify-between" data-testid="age-18-banner">
      <div>
        <p className="text-sm font-semibold text-amber-800">You just turned 18.</p>
        <p className="text-xs text-amber-600">These experiences just became available to you.</p>
      </div>
      <button onClick={onDismiss} className="text-amber-400 text-xs font-medium">Dismiss</button>
    </motion.div>
  );
}
